import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { ColumnFilterRow, SortableHeaderRow, TableToolbar, useTableTools } from "@/components/TableFilters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { razon_social: "", rfc: "", contacto: "", email: "", telefono: "", condiciones_credito_dias: 30, datos_bancarios: "" };

const COLUMNS = [
  { key: "razon", label: "Razón Social", accessor: (s) => s.razon_social, placeholder: "Razón social" },
  { key: "rfc", label: "RFC", accessor: (s) => s.rfc, placeholder: "RFC" },
  { key: "contacto", label: "Contacto", accessor: (s) => `${s.contacto || ""} ${s.email || ""}`, placeholder: "Contacto" },
  { key: "credito", label: "Crédito", accessor: (s) => s.condiciones_credito_dias, placeholder: "Días" },
  { key: "_a", type: "none" },
];

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const tools = useTableTools(rows, COLUMNS);

  const load = () => api.get("/suppliers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success("Proveedor guardado"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold tracking-tight">Proveedores</h1><p className="text-muted-foreground text-sm">Expediente y condiciones de crédito.</p></div>
        <div className="flex items-center gap-2">
          <TableToolbar tools={tools} columns={COLUMNS} filename="proveedores" />
          <Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="new-supplier-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo proveedor</Button>
        </div>
      </div>
      <Card className="rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <SortableHeaderRow columns={COLUMNS} sort={tools.sort} toggleSort={tools.toggleSort} />
            {tools.showFilters && <ColumnFilterRow columns={COLUMNS} filters={tools.filters} setFilters={tools.setFilters} />}
          </TableHeader>
          <TableBody>
            {tools.view.map((s) => (
              <TableRow key={s.id} data-testid="supplier-row">
                <TableCell className="font-medium">{s.razon_social}</TableCell>
                <TableCell className="font-mono text-xs">{s.rfc}</TableCell>
                <TableCell>{s.contacto}<div className="text-xs text-muted-foreground">{s.email}</div></TableCell>
                <TableCell>{s.condiciones_credito_dias} días</TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => { setForm({ ...empty, ...s }); setEditId(s.id); setOpen(true); }}><Pencil className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sin proveedores aún.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Editar" : "Nuevo"} proveedor</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Razón Social *</Label><Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} data-testid="supplier-razon" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>RFC</Label><Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} /></div>
              <div><Label>Días de crédito</Label><Input type="number" value={form.condiciones_credito_dias} onChange={(e) => setForm({ ...form, condiciones_credito_dias: Number(e.target.value) })} /></div>
              <div><Label>Contacto</Label><Input value={form.contacto} onChange={(e) => setForm({ ...form, contacto: e.target.value })} /></div>
              <div><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
            </div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Datos bancarios</Label><Textarea value={form.datos_bancarios} onChange={(e) => setForm({ ...form, datos_bancarios: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-supplier">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
